<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

import type { RetentionEspType } from '@/stores/onboarding-retention-progress'

import cartseeLogo from '@/assets/icons/integrations/cartsee.png'
import manbaLogo from '@/assets/icons/integrations/manba.png'
import klaviyoLogo from '@/assets/klaviyo.png'
import attribulyLogo from '@/assets/pics/attribuly-logo-white.png'
import { Button } from '@/components/ui/button'
import { useAmplitude } from '@/composables/use-amplitude'
import {

  useOnboardingRetentionProgressStore,
} from '@/stores/onboarding-retention-progress'

definePage({
  path: '/onboarding-retention/step0',
  meta: {
    layout: false,
    title: 'Choose your ESP',
  },
})

const router = useRouter()
const { t, locale } = useI18n()
const progress = useOnboardingRetentionProgressStore()
const { track } = useAmplitude()

const selected = ref<RetentionEspType | null>(null)
const isSaving = ref(false)
const loadFailed = ref(false)

const options: Array<{ id: RetentionEspType, logo: string, logoStyle?: string }> = [
  { id: 'klaviyo', logo: klaviyoLogo },
  { id: 'manba', logo: manbaLogo },
  { id: 'omnisend', logo: '/cherry/omnisend.svg', logoStyle: 'width: 36px; height: 36px;' },
  { id: 'cartsee', logo: cartseeLogo },
]

/**
 * 选择器的显示/点击高亮逻辑：
 *
 * 「本次会话中用户在 UI 上的选择」优先；
 * 否则使用「已经由 Continue -> save() 成功落库 / backend progress_data 里返回的 selectedEsp」
 * 否则默认 Klaviyo（因为要求 Step 0 默认选中 Klaviyo）。
 *
 * 这里的关键点：点击 selectOption() 后会立刻写 selected.value，
 * 所以下一次 computed 重新计算时，新的 UI 选中会在首位，视觉立刻更新 + Continue 可用。
 */
const selectedEsp = computed<RetentionEspType | null>(() => {
  return (selected.value as RetentionEspType)
    ?? (progress.resolvedEsp as RetentionEspType | null)
    ?? 'klaviyo'
})

function toggleLocale() {
  const next = locale.value === 'en' ? 'zh' : 'en'
  locale.value = next
  localStorage.setItem('locale', next)
  if (typeof document !== 'undefined') {
    document.documentElement.lang = next
  }
}

function selectOption(id: RetentionEspType) {
  if (isSaving.value)
    return
  selected.value = id
  /**
   * 清理本地缓存中的旧 ESP：
   * - 避免用户"在 step0 中点过一次 Klaviyo → 没 Continue 就返回 Shopify → 重开 App → 想选 Omnisend 但永远变不成"
   * - 真正的持久化只在 Continue 点击且 save() 成功之后才由 setEsp()（带 sessionStorage 写）发生
   *   在此之前，用户可以任意切换直到满足他的期望。
   */
  progress.clearEspSessionCache()
}

async function loadBackendProgressOnce() {
  try {
    await progress.loadFromBackend()
    /**
     * 只有在 backend 已经明确记录了 selectedEsp（即用户在此前的会话中成功点过 Continue），
     * 才把本地 selected 去掉 → 让 savedEsp 的 UI 高亮保留，用户无需再选。
     *
     * 否则（backend selectedEsp 空 = 之前没 Continue）：保留默认 klaviyo，但允许重新切换。
     */
    if (!progress.resolvedEsp)
      selected.value = 'klaviyo'
    loadFailed.value = false
  }
  catch (e) {
    console.warn('[retention-onboarding] step0 loadFromBackend failed, use local selection only:', e)
    loadFailed.value = true
    selected.value = selected.value || 'klaviyo'
  }
}

/**
 * 与 step1.vue 的「先导航，save 异步 fire forget」不同，
 * 这里因为是 ESP 的初次提交，必须先保证 backend 保存成功，再进入下一步。
 * 原因：
 *  - Step0 → Step1 的守卫 / ensureLoaded 依赖 progress_data.selectedEsp，
 *    它只能从后端 loadFromBackend 回来；若仅 sessionStorage 有、后端没有，
 *    Step1 的 ensureLoaded 会把用户踢回 step0 → 表现为卡死。
 *
 * 为了不让"接口 500"阻碍 UX，退而求其次：save() 失败时把 sessionStorage
 * 缓存上，再跳 step1。Step1 的 ensureLoaded 会先读 backend，若 backend 回空，
 * 再 fallback 到 sessionStorage。
 */
async function handleContinue() {
  const pick = selectedEsp.value
  if (!pick || isSaving.value)
    return

  try {
    track('retention_onboarding_step0_start_email_recovery_click', { esp: pick })
  }
  catch (error) {
    console.warn('[retention-onboarding] step0 amplitude start email recovery click track failed:', error)
  }

  isSaving.value = true
  try {
    progress.setEsp(pick) // 先写本地 + sessionStorage 作为兜底
    try {
      await progress.save({
        current_step: 1,
        current_platform: 'retention',
        progress_data: { selectedEsp: pick },
      })
    }
    catch (saveError) {
      console.warn('[retention-onboarding] step0 save progress failed (session cache persisted, navigating to step1 anyway):', saveError)
      // 不 throw，sessionStorage 已经写入了，step1 ensureLoaded 能读到
    }
    router.push('/onboarding-retention/step1')
  }
  finally {
    isSaving.value = false
  }
}

function trackBookDemoClick() {
  try {
    track('q_source_book_demo_click', { step: 'step0' })
  }
  catch (error) {
    console.warn('[retention-onboarding] step0 amplitude book demo click track failed:', error)
  }
}

function handleAttributionOnlyClick() {
  try {
    track('retention_step0_attribution_only_goal_first_click')
  }
  catch (error) {
    console.warn('[retention-onboarding] step0 amplitude attribution only track failed:', error)
  }
  router.push('/onboarding')
}

/**
 * 如果用户之前在 step0 打开的过程里，被 Shopify App 打断（浏览器 Tab 保留，内容没刷），
 * 再次从 Shopify 后台点开时，页面在 visibilitychange 时重拉 backend progress，
 * 保证 savedEsp 状态与后端同步。
 */
function onVisibilityChange() {
  if (document.visibilityState === 'visible')
    void loadBackendProgressOnce()
}

onMounted(() => {
  try {
    track('retention_onboarding_step0_pageview', { esp: selectedEsp.value ?? 'klaviyo' })
  }
  catch (error) {
    console.warn('[retention-onboarding] step0 amplitude pageview track failed:', error)
  }
  void loadBackendProgressOnce()
  if (typeof document !== 'undefined')
    document.addEventListener('visibilitychange', onVisibilityChange)
})
onBeforeUnmount(() => {
  if (typeof document !== 'undefined')
    document.removeEventListener('visibilitychange', onVisibilityChange)
})

// 避免 "selectedEsp 被错误地和后端 progress 绑定死" 的额外保险：
// 当 store 的 selectedEsp 非空、且 backend progress_data.selectedEsp 为空，
// 说明其实后端从未保存过（上一轮的 resolveRetentionRedirectPath 只是 hydration 了 session）
// → 手动清掉 store.selectedEsp 的 ref 锁，允许本地 UI 通过 selected.value 自由切换。
watch(
  () => [progress.loaded, (progress.progressData as Record<string, any>).selectedEsp] as const,
  ([loaded, espFromProgress]) => {
    if (!loaded)
      return
    if (espFromProgress)
      return
    if (progress.selectedEsp && !selected.value) {
      progress.clearEspSessionCache()
    }
  },
  { immediate: true },
)
</script>

<template>
  <div class="fixed inset-0 w-full bg-[#0E0F0D] text-white overflow-y-auto">
    <div
      class="pointer-events-none absolute inset-0 opacity-[0.35]"
      style="background-image: radial-gradient(60% 50% at 50% 0%, rgba(40, 180, 120, 0.18) 0%, transparent 60%), radial-gradient(40% 30% at 85% 20%, rgba(80, 160, 255, 0.14) 0%, transparent 60%), radial-gradient(50% 40% at 15% 85%, rgba(255, 140, 90, 0.12) 0%, transparent 60%);"
    />

    <header class="relative z-10 flex items-center justify-between px-8 py-6">
      <img :src="attribulyLogo" alt="Attribuly" class="h-5 w-auto">
      <Button
        variant="ghost"
        size="icon-sm"
        class="text-white/70 hover:text-white hover:bg-white/10"
        :aria-label="t('onboardingRetention.sidebar.toggleLanguageAria')"
        :title="t('onboardingRetention.sidebar.toggleLanguageAria')"
        @click="toggleLocale"
      >
        <span class="text-xs font-semibold tracking-wide uppercase">
          {{ locale === 'en' ? '中' : 'EN' }}
        </span>
      </Button>
    </header>

    <main class="relative z-10 mx-auto flex max-w-5xl flex-col items-center px-6 pb-24 pt-6">
      <div class="text-center">
        <div v-if="false" class="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-white/60">
          <span class="h-1.5 w-1.5 rounded-full bg-emerald-400" />
          {{ t('onboardingRetention.center.kicker') }}
        </div>

        <h1 class="text-3xl font-semibold leading-tight tracking-tight sm:text-4xl md:text-[2.6rem]">
          {{ t('onboardingRetention.step0.title') }}
        </h1>
        <p class="mx-auto mt-4 max-w-2xl text-[15px] leading-relaxed text-white/55">
          {{ t('onboardingRetention.step0.subtitle') }}
        </p>
      </div>

      <div class="mt-12 grid w-full max-w-3xl mx-auto gap-4 sm:grid-cols-2 md:grid-cols-4">
        <button
          v-for="opt in options"
          :key="opt.id"
          type="button"
          class="group relative flex h-full flex-col rounded-[var(--p-border-radius-300)] border p-5 text-left transition-all duration-200"
          :class="[
            selectedEsp === opt.id
              ? 'border-emerald-400/60 bg-white/[0.06] shadow-[0_0_0_1px_rgba(52,211,153,0.3)]'
              : 'border-white/10 bg-white/[0.025] hover:border-white/20 hover:bg-white/[0.04]',
          ]"
          @click="selectOption(opt.id)"
        >
          <div
            class="absolute left-0 top-0 h-1 w-full rounded-t-2xl opacity-0 transition-opacity"
            :class="selectedEsp === opt.id ? 'opacity-100' : ''"
            :style="{ background: 'linear-gradient(90deg, transparent, rgba(52,211,153,0.55), transparent)' }"
          />

          <div class="flex items-center gap-3">
            <div
              class="flex h-11 w-11 shrink-0 items-center justify-center rounded-[var(--p-border-radius-300)] bg-white"
            >
              <img
                :src="opt.logo"
                :alt="t(`onboardingRetention.espBrandNames.${opt.id}`)"
                class="max-h-7 max-w-7 object-contain"
                :style="opt.logoStyle"
              >
            </div>
            <h3 class="text-base font-semibold tracking-tight text-white">
              {{ t(`onboardingRetention.step0.options.${opt.id}.title`) }}
            </h3>
          </div>

          <div
            class="mt-5 w-full rounded-[var(--p-border-radius-200)] text-center text-[12px] font-medium py-2 transition-colors"
            :class="
              selectedEsp === opt.id
                ? 'bg-emerald-400/10 text-emerald-300'
                : 'bg-white/[0.03] text-white/40 group-hover:text-white/60'
            "
          >
            <template v-if="selectedEsp === opt.id">
              {{ t('onboardingRetention.step0.selectedCta') }}
            </template>
            <template v-else>
              {{ t('onboardingRetention.step0.selectCta') }}
            </template>
          </div>
        </button>
      </div>

      <div class="mt-10 flex w-full flex-col items-center gap-5">
        <Button
          size="lg"
          class="min-w-[220px] rounded-[var(--p-border-radius-200)] border-0 bg-emerald-500 text-[15px] font-semibold text-white shadow-none hover:bg-emerald-600 hover:text-white"
          :disabled="!selectedEsp"
          @click="handleContinue"
        >
          {{ t('onboardingRetention.step0.primaryCta') }}
        </Button>
        <div class="text-center text-sm leading-snug text-white/50">
          <span>{{ t('onboardingRetention.step0.bookDemo.prefix') }}</span>
          <a
            href="https://attribuly.com/scheduler/?utm_source=onboarding&utm_campaign=step0"
            target="_blank"
            rel="noopener noreferrer"
            class="ml-1 inline-flex items-center font-semibold text-white underline underline-offset-2 hover:text-white/80"
            @click="trackBookDemoClick"
          >
            {{ t('onboardingRetention.step0.bookDemo.action') }}
          </a>
          <span>{{ t('onboardingRetention.step0.bookDemo.suffix') }}</span>
        </div>
        <div class="text-center text-sm leading-snug text-white/50">
          <span>{{ t('onboardingRetention.step0.attributionOnly.prefix') }}</span>
          <button
            type="button"
            class="ml-1 inline-flex items-center font-semibold text-white underline underline-offset-2 hover:text-white/80"
            @click="handleAttributionOnlyClick"
          >
            {{ t('onboardingRetention.step0.attributionOnly.action') }}
          </button>
        </div>
      </div>
    </main>
  </div>
</template>
